import { useEffect, useRef, useMemo } from "react";
import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { toast } from "sonner";
import type { ApplicationGuildSettings } from "@discord-bldr/shared";

export interface CachedChannel {
  id: string;
  type: number;
  name: string;
  position: number;
  parentId: string | null;
  availableTags?: Array<{
    id: string;
    name: string;
    emojiId: string | null;
    emojiName: string | null;
  }>;
}

export interface CachedRole {
  id: string;
  name: string;
  color: number;
  position: number;
  permissions: string;
}

export interface CachedEmoji {
  id: string;
  name: string;
  animated: boolean;
  url: string;
}

export interface GuildChannelsResponse {
  data: {
    textChannels: CachedChannel[];
    forumChannels: CachedChannel[];
    categories: CachedChannel[];
  };
  meta: {
    cached: boolean;
    cachedAt: number;
    expiresAt: number;
  };
}

export interface GuildRolesResponse {
  data: CachedRole[];
  meta: {
    cached: boolean;
    cachedAt: number;
    expiresAt: number;
  };
}

export interface GuildEmojisResponse {
  data: CachedEmoji[];
  meta: {
    cached: boolean;
    cachedAt: number;
    expiresAt: number;
  };
}

export type GuildSettings = ApplicationGuildSettings;

export interface KnowledgeBaseStatus {
  applicationGuildId: string;
  settings: ApplicationGuildSettings | null;
  documentsCount: number;
  personality: string | null;
  isConfigured: boolean;
}

export interface KnowledgeBaseStatusResponse {
  data: KnowledgeBaseStatus;
}

export interface Document {
  id: number;
  applicationGuildId: string;
  sourceType: string;
  sourceUrl: string | null;
  content: string;
  metadata: string | null;
  addedBy: string;
  title: string | null;
  tags: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentsListResponse {
  data: {
    documents: Document[];
    total: number;
    limit: number;
    offset: number;
  };
}

export interface DocumentResponse {
  data: Document;
}

export interface PersonalityResponse {
  data: {
    personality: string;
  };
}

export interface UpdateSettingsResponse {
  data: {
    settings: GuildSettings;
  };
}

export interface EvaluationMetrics {
  totalQueries: number;
  precisionAt5: number;
  recallAt5: number;
  mrr: number;
  averageSimilarity: number;
}

export interface RagEvaluationRun extends EvaluationMetrics {
  id: number;
  applicationGuildId: string;
  configSnapshot: Record<string, unknown> | null;
  createdAt: string;
}

export interface EvaluationHistoryResponse {
  data: RagEvaluationRun[];
}

export interface EvaluateResponse {
  data: EvaluationMetrics;
}

export const knowledgeBaseKeys = {
  all: ["knowledge-base"] as const,
  status: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "status", applicationGuildId] as const,
  channels: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "channels", applicationGuildId] as const,
  roles: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "roles", applicationGuildId] as const,
  emojis: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "emojis", applicationGuildId] as const,
  documents: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "documents", applicationGuildId] as const,
  document: (applicationGuildId: string, documentId: number) =>
    [
      ...knowledgeBaseKeys.all,
      "document",
      applicationGuildId,
      documentId,
    ] as const,
  personality: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "personality", applicationGuildId] as const,
  evaluation: (applicationGuildId: string) =>
    [...knowledgeBaseKeys.all, "evaluation", applicationGuildId] as const,
};

export const knowledgeBaseStatusQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.status(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<KnowledgeBaseStatusResponse>(
          `/api/guilds/${applicationGuildId}/knowledge-base`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useKnowledgeBaseStatus(applicationGuildId: string) {
  return useQuery(knowledgeBaseStatusQueryOptions(applicationGuildId));
}

export const guildChannelsQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.channels(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<GuildChannelsResponse>(
          `/api/guilds/${applicationGuildId}/channels`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useGuildChannels(applicationGuildId: string) {
  return useQuery(guildChannelsQueryOptions(applicationGuildId));
}

export const guildRolesQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.roles(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<GuildRolesResponse>(`/api/guilds/${applicationGuildId}/roles`)
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useGuildRoles(applicationGuildId: string) {
  return useQuery(guildRolesQueryOptions(applicationGuildId));
}

export const guildEmojisQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.emojis(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<GuildEmojisResponse>(`/api/guilds/${applicationGuildId}/emojis`)
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useGuildEmojis(applicationGuildId: string) {
  return useQuery(guildEmojisQueryOptions(applicationGuildId));
}

export interface DocumentsFilterOptions {
  limit?: number;
  offset?: number;
  search?: string;
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
}

const documentsQueryOptions = (
  applicationGuildId: string,
  options: DocumentsFilterOptions
) => {
  const { limit = 20, offset = 0, ...filters } = options;
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }

  return queryOptions({
    queryKey: [
      ...knowledgeBaseKeys.documents(applicationGuildId),
      { limit, offset, ...filters },
    ],
    queryFn: () =>
      apiClient
        .get<DocumentsListResponse>(
          `/api/guilds/${applicationGuildId}/documents?${params.toString()}`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
    placeholderData: keepPreviousData,
  });
};

export function useDocuments(
  applicationGuildId: string,
  options: DocumentsFilterOptions = {}
) {
  return useQuery(documentsQueryOptions(applicationGuildId, options));
}

const INFINITE_DOCS_PAGE_SIZE = 50;

export function useInfiniteDocuments(
  applicationGuildId: string,
  search?: string
) {
  return useInfiniteQuery({
    queryKey: [
      ...knowledgeBaseKeys.documents(applicationGuildId),
      "infinite",
      { search },
    ],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        limit: String(INFINITE_DOCS_PAGE_SIZE),
        offset: String(pageParam),
      });
      if (search) params.set("search", search);
      return apiClient
        .get<DocumentsListResponse>(
          `/api/guilds/${applicationGuildId}/documents?${params.toString()}`
        )
        .then((r) => r.data);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { offset, limit, total } = lastPage.data;
      const nextOffset = offset + limit;
      return nextOffset < total ? nextOffset : undefined;
    },
    enabled: !!applicationGuildId,
  });
}

const documentQueryOptions = (applicationGuildId: string, documentId: number) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.document(applicationGuildId, documentId),
    queryFn: () =>
      apiClient
        .get<DocumentResponse>(
          `/api/guilds/${applicationGuildId}/documents/${documentId}`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId && !!documentId,
  });

export function useDocument(applicationGuildId: string, documentId: number) {
  return useQuery(documentQueryOptions(applicationGuildId, documentId));
}

const personalityQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.personality(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<PersonalityResponse>(
          `/api/guilds/${applicationGuildId}/personality`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function usePersonality(applicationGuildId: string) {
  return useQuery(personalityQueryOptions(applicationGuildId));
}

export function useCreateDocument(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      content: string;
      title?: string;
      sourceType?: "direct" | "message" | "url";
      sourceUrl?: string;
    }) =>
      apiClient
        .post<DocumentResponse>(
          `/api/guilds/${applicationGuildId}/documents`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documents(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.personality(applicationGuildId),
      });
    },
  });
}

export function useDeleteDocument(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: number) =>
      apiClient
        .delete<{
          data: { success: boolean; id: number };
        }>(`/api/guilds/${applicationGuildId}/documents/${documentId}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documents(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.personality(applicationGuildId),
      });
    },
  });
}

export function useBulkDeleteDocuments(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: number[]) =>
      apiClient
        .post<{
          data: { success: boolean; deletedCount: number };
        }>(`/api/guilds/${applicationGuildId}/documents/bulk-delete`, { ids })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documents(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.personality(applicationGuildId),
      });
    },
  });
}

export function useRefreshPersonality(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient
        .post<PersonalityResponse>(
          `/api/guilds/${applicationGuildId}/personality/refresh`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.personality(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
    },
  });
}

export interface ResyncResponse {
  data: {
    queued: number;
    lastResyncAt: string;
  };
}

export function useResyncDocuments(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient
        .post<ResyncResponse>(
          `/api/guilds/${applicationGuildId}/documents/resync`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.all,
      });
    },
  });
}

export function useUpdateGuildSettings(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<GuildSettings>) =>
      apiClient
        .patch<UpdateSettingsResponse>(
          `/api/guilds/${applicationGuildId}/settings`,
          settings
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
    },
  });
}

export interface ScrapeJobResponse {
  data: {
    jobId: string;
    status: string;
    url: string;
  };
}

export interface JobStatusResponse {
  data: {
    jobId: string;
    status: "pending" | "active" | "completed" | "failed";
    progress?: number;
    currentStep?: string;
    message?: string;
    result?: {
      documentId?: number;
      chunkCount?: number;
      urls?: string[];
    };
    error?: string;
  };
}

export interface ScrapingStatusResponse {
  data: {
    isAvailable: boolean;
  };
}

export function useScrapeUrl(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (url: string) =>
      apiClient
        .post<ScrapeJobResponse>(
          `/api/guilds/${applicationGuildId}/documents/scrape`,
          {
            url,
          }
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.all, "scrape-jobs", applicationGuildId],
      });
    },
  });
}

export interface BulkScrapeResult {
  url: string;
  jobId?: string;
  status: "queued" | "skipped";
  error?: string;
}

export interface BulkScrapeResponse {
  data: {
    results: BulkScrapeResult[];
    queued: number;
    skipped: number;
  };
}

export const officialPresetsQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: [
      ...knowledgeBaseKeys.all,
      "official-presets",
      applicationGuildId,
    ],
    queryFn: () =>
      apiClient
        .get<{
          data: import("@/lib/api/types").OfficialDocumentPreset[];
          total: number;
        }>(`/api/guilds/${applicationGuildId}/official-presets`)
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useOfficialPresets(applicationGuildId: string) {
  return useQuery(officialPresetsQueryOptions(applicationGuildId));
}

export function useApplyOfficialPreset(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (presetId: number) =>
      apiClient
        .post<BulkScrapeResponse>(
          `/api/guilds/${applicationGuildId}/official-presets/${presetId}/apply`
        )
        .then((r) => r.data),
    onSuccess: (data) => {
      const { queued, skipped } = data.data;
      if (queued > 0) {
        toast.success(
          `Scraping ${queued} URL${queued > 1 ? "s" : ""}${skipped > 0 ? ` (${skipped} already indexed)` : ""}`
        );
      } else {
        toast.info("All URLs from this preset are already indexed");
      }
      queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.all, "scrape-jobs", applicationGuildId],
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documents(applicationGuildId),
      });
    },
    onError: () => {
      toast.error("Failed to apply preset");
    },
  });
}

export interface DiscoverUrlsResponse {
  data: {
    jobId: string;
    urls?: string[];
    indexedUrls?: string[];
  };
}

export function useDiscoverUrls(applicationGuildId: string) {
  return useMutation({
    mutationFn: (params: {
      url: string;
      options?: { maxDepth?: number; maxPages?: number };
    }) =>
      apiClient
        .post<DiscoverUrlsResponse>(
          `/api/guilds/${applicationGuildId}/documents/discover-urls`,
          params
        )
        .then((r) => r.data),
  });
}

export function useBulkScrapeUrls(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urls: string[]) =>
      apiClient
        .post<BulkScrapeResponse>(
          `/api/guilds/${applicationGuildId}/documents/scrape/bulk`,
          { urls }
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.all, "scrape-jobs", applicationGuildId],
      });
    },
  });
}

const jobStatusQueryOptions = (
  applicationGuildId: string,
  jobId: string | null
) =>
  queryOptions({
    queryKey: [...knowledgeBaseKeys.all, "job", applicationGuildId, jobId],
    queryFn: () =>
      apiClient
        .get<JobStatusResponse>(
          `/api/guilds/${applicationGuildId}/documents/scrape/${jobId}`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId && !!jobId,
  });

export function useJobStatus(applicationGuildId: string, jobId: string | null) {
  return useQuery(jobStatusQueryOptions(applicationGuildId, jobId));
}

const scrapingStatusQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: [...knowledgeBaseKeys.all, "scraping-status", applicationGuildId],
    queryFn: () =>
      apiClient
        .get<ScrapingStatusResponse>(
          `/api/guilds/${applicationGuildId}/scraping-status`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
    staleTime: 30 * 1000,
  });

export function useScrapingStatus(applicationGuildId: string) {
  return useQuery(scrapingStatusQueryOptions(applicationGuildId));
}

export interface ScrapeJob {
  jobId: string;
  status: "pending" | "active" | "completed" | "failed";
  progress?: number;
  currentStep?: string;
  message?: string;
  url?: string;
  result?: {
    documentId?: number;
    chunkCount?: number;
  };
  error?: string;
  timestamps: {
    created: string;
    started?: string;
    completed?: string;
    failed?: string;
  };
}

export interface ScrapeJobsResponse {
  data: {
    jobs: ScrapeJob[];
  };
}

const scrapeJobsQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: [...knowledgeBaseKeys.all, "scrape-jobs", applicationGuildId],
    queryFn: () =>
      apiClient
        .get<ScrapeJobsResponse>(
          `/api/guilds/${applicationGuildId}/scrape-jobs`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useDismissScrapeJob(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) =>
      apiClient.delete(
        `/api/guilds/${applicationGuildId}/scrape-jobs/${jobId}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...knowledgeBaseKeys.all, "scrape-jobs", applicationGuildId],
      });
    },
  });
}

export const evaluationHistoryQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: knowledgeBaseKeys.evaluation(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<EvaluationHistoryResponse>(
          `/api/guilds/${applicationGuildId}/rag/evaluate/history`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useEvaluationHistory(applicationGuildId: string) {
  return useQuery(evaluationHistoryQueryOptions(applicationGuildId));
}

export function useRunEvaluation(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient
        .post<EvaluateResponse>(
          `/api/guilds/${applicationGuildId}/rag/evaluate`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.evaluation(applicationGuildId),
      });
    },
  });
}

export function useScrapeJobs(applicationGuildId: string) {
  const queryClient = useQueryClient();
  const activeJobIds = useRef<Set<string>>(new Set());

  const query = useQuery({
    ...scrapeJobsQueryOptions(applicationGuildId),
    refetchInterval: (query) => {
      const jobs = query.state.data?.data?.jobs || [];
      const hasActiveJobs = jobs.some(
        (j) => j.status === "pending" || j.status === "active"
      );
      return hasActiveJobs ? 5000 : false;
    },
  });

  const jobs = useMemo(
    () => query.data?.data?.jobs || [],
    [query.data?.data?.jobs]
  );

  // Detect completed jobs and invalidate documents
  useEffect(() => {
    const currentActiveIds = new Set(
      jobs
        .filter((j) => j.status === "pending" || j.status === "active")
        .map((j) => j.jobId)
    );

    const completedJobs = jobs.filter(
      (j) => j.status === "completed" && activeJobIds.current.has(j.jobId)
    );

    if (completedJobs.length > 0) {
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documents(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.personality(applicationGuildId),
      });
    }

    activeJobIds.current = currentActiveIds;
  }, [jobs, applicationGuildId, queryClient]);

  return query;
}
